import React, { useState, useEffect, Fragment } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-hot-toast';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { PlusCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import AddCompanyModal from '../features/dashboard/jobs/AddCompanyModal';
import ReactDOM from 'react-dom';

// Render AddCompanyModal in a Portal to avoid stacking context issues
function AddCompanyModalPortal({ isOpen, onClose, onCompanyAdded }) {
  return ReactDOM.createPortal(
    <AddCompanyModal 
      isOpen={isOpen}
      onClose={onClose}
      onCompanyAdded={onCompanyAdded}
      userId={null}
      zIndex={100}
    />,
    document.body
  );
}

export default function JobFormModal({ job, isOpen, onClose, onSave, isLoading }) {
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    companyId: '', // Add companyId field to store the selected company's ID
    location: '',
    type: 'full-time', // Corresponds to locationType in schema (on-site/remote/hybrid) + job type
    description: '',
    requirements: '', // Corresponds to requiredSkills
    responsibilities: '',
    status: 'active',
    source: 'admin',
    applicationUrl: '',
    // New fields from schema
    experienceLevel: '', // e.g., 'entry', 'mid', 'senior'
    salary: '',
    industry: '',
    roleType: '', // e.g., 'technical', 'non-technical'
    workEnvironment: '',
    applicationDeadline: '',
    postedDate: '',
    summary: '', // Added summary field
    workplace: '', // Added workplace field
  });
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [jobText, setJobText] = useState('');
  const [extractionMethod, setExtractionMethod] = useState('text'); // 'url' or 'text'
  const [extractedUrlContent, setExtractedUrlContent] = useState(''); // Store content extracted from URL
  
  // Company search and selection states
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState(false);
  // Track if company field has been interacted with
  const [companyFieldTouched, setCompanyFieldTouched] = useState(false);

  // Reset form data when job changes or for new job
  useEffect(() => {
    const defaultState = {
      title: '',
      company: '',
      companyId: '',
      location: '',
      type: 'full-time',
      description: '',
      requirements: '',
      responsibilities: '',
      status: 'active',
      source: 'admin',
      applicationUrl: '',
      experienceLevel: '',
      salary: '',
      industry: '',
      roleType: '',
      workEnvironment: '',
      applicationDeadline: '',
      postedDate: '',
      summary: '', // Added default summary
      workplace: '', // Added default workplace
    };

    if (job) {
      // Map job properties to formData, handling potential differences/missing fields
      setFormData({
        ...defaultState, // Start with defaults
        title: job.title || '',
        company: job.company || '',
        companyId: job.companyId || '',
        location: job.location || '',
        type: job.type || 'full-time',
        description: job.description || '',
        // Join arrays if needed, otherwise use string
        requirements: Array.isArray(job.requirements) ? job.requirements.join('\n') : (job.requirements || ''),
        responsibilities: Array.isArray(job.responsibilities) ? job.responsibilities.join('\n') : (job.responsibilities || ''),
        status: job.status || 'active',
        source: job.source || 'admin',
        applicationUrl: job.applicationUrl || '',
        experienceLevel: job.experienceLevel || '',
        salary: job.salary || '',
        industry: job.industry || '',
        roleType: job.roleType || '',
        workEnvironment: job.workEnvironment || '',
        applicationDeadline: job.applicationDeadline || '',
        postedDate: job.postedDate || '',
        summary: job.summary || '', // Added summary mapping
        workplace: job.workplace || '', // Added workplace mapping
      });
      
      // If job has a company ID, try to find it in the companies list
      if (job.companyId) {
        const company = companies.find(c => c.id === job.companyId);
        if (company) {
          setSelectedCompany(company);
        }
      } else if (job.company) {
        // For legacy jobs with only company name (text) but no companyId
        // Try to find a matching company by name
        const matchingCompany = companies.find(
          c => c.name.toLowerCase() === job.company.toLowerCase()
        );
        
        if (matchingCompany) {
          // If we find a matching company by name, use it
          setSelectedCompany(matchingCompany);
          // Don't update formData here - we'll do that only when user explicitly selects a company
        } else {
          // No matching company found, clear selection but keep the text value
          setSelectedCompany(null);
          // Initialize the search with the existing company name to help find matches
          setCompanySearchQuery(job.company);
        }
      }
    } else {
      // Reset form for new job
      setFormData(defaultState);
      setSelectedCompany(null);
    }
  }, [job, companies]);
  
  // Fetch companies on component mount
  useEffect(() => {
    fetchCompanies();
  }, []);
  
  // Filter companies when search query changes
  useEffect(() => {
    if (companySearchQuery === '') {
      setFilteredCompanies(companies);
    } else {
      const filtered = companies.filter(company => 
        company.name.toLowerCase().includes(companySearchQuery.toLowerCase()) ||
        (company.industry && company.industry.toLowerCase().includes(companySearchQuery.toLowerCase()))
      );
      setFilteredCompanies(filtered);
    }
  }, [companySearchQuery, companies]);
  
  // Fetch companies from Firestore
  const fetchCompanies = async () => {
    try {
      const db = getFirestore();
      const companiesCollection = collection(db, 'companies');
      const companiesSnapshot = await getDocs(companiesCollection);
      
      const companiesList = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setCompanies(companiesList);
      setFilteredCompanies(companiesList);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies list');
    }
  };

  // Handle selecting a company from the dropdown
  const handleSelectCompany = (company) => {
    if (!company) return; // Skip if company is null
    
    setSelectedCompany(company);
    setCompanyFieldTouched(true);
    
    // Update form data with the selected company
    setFormData(prev => ({
      ...prev, // Keep all existing form data
      company: company.name,
      companyId: company.id,
      industry: company.industry || prev.industry
    }));
  };
  
  // Handle adding a new company
  const handleAddNewCompany = (newCompany) => {
    // Add the new company to the companies list
    setCompanies(prevCompanies => [...prevCompanies, newCompany]);
    
    // Set the new company as the selected company
    setSelectedCompany(newCompany);
    setFormData({
      ...formData,
      company: newCompany.name,
      companyId: newCompany.id,
      industry: newCompany.industry || formData.industry
    });
    
    // Close the Add Company modal
    setIsAddCompanyModalOpen(false);
    
    // Show success message
    toast.success('Company added successfully!');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Only include id when editing an existing job (job is defined)
    if (job) {
      onSave({ ...formData, id: job.id });
    } else {
      // For new jobs, don't include the id field
      onSave({ ...formData });
    }
  };

  const handleFetchJobDetails = async () => {
    if (extractionMethod === 'url') {
      // Validate URL first
      if (!formData.applicationUrl.trim()) {
        setFetchError('Please enter a job URL first');
        toast.error('Please enter a job URL first');
        return;
      }
      
      if (!/^https?:\/\/.+/i.test(formData.applicationUrl)) {
        setFetchError('Please enter a valid URL starting with http:// or https://');
        toast.error('Please enter a valid URL starting with http:// or https://');
        return;
      }
    } else {
      // Validate text input
      if (!jobText.trim()) {
        setFetchError('Please enter job description text');
        toast.error('Please enter job description text');
        return;
      }
      
      if (jobText.trim().length < 50) {
        setFetchError('Job description text is too short. Please provide more details.');
        toast.error('Job description text is too short. Please provide more details.');
        return;
      }
    }
    
    setIsFetchingDetails(true);
    setFetchError('');
    
    try {
      const functions = getFunctions();
      let result;
      
      // Always use the extractJobDetails function with jobDescription parameter
      const extractJobDetails = httpsCallable(functions, 'extractJobDetails');
      
      if (extractionMethod === 'url') {
        // For URL extraction, we need to handle the fact that many job sites block scraping
        // Instead of trying to fetch the URL content on the server, we'll just use the URL as a reference
        // and prompt the user to paste the content if extraction fails
        
        // Store the URL in the application URL field for reference
        setFormData(prev => ({ ...prev, applicationUrl: formData.applicationUrl }));
        
        // Try to extract job details using just the URL as a reference
        // This will likely fail for sites with anti-scraping measures
        result = await extractJobDetails({ jobDescription: formData.applicationUrl });
      } else {
        // For text-based extraction, send the job text as the description
        result = await extractJobDetails({ jobDescription: jobText });
      }
      
      const jobDetails = result.data;
      
      console.log('Extracted job details:', jobDetails);
      
      // Update form with extracted data
      const updatedFormData = {
        ...formData,
        title: jobDetails.job_title || formData.title,
        company: jobDetails.company_name || formData.company,
        location: jobDetails.location || formData.location,
        // Map job_type to type field
        type: jobDetails.job_type?.toLowerCase() || formData.type,
        // Use summary as description when available, fall back to description field
        description: jobDetails.summary || jobDetails.description || formData.description,
        // Handle responsibilities: use directly if string, keep existing if null/undefined
        responsibilities: typeof jobDetails.responsibilities === 'string' ? jobDetails.responsibilities : formData.responsibilities,
        // Handle requirements: use directly if string, keep existing if null/undefined
        requirements: typeof jobDetails.requirements === 'string' ? jobDetails.requirements : formData.requirements,
        experienceLevel: jobDetails.experienceLevel || formData.experienceLevel,
        salary: jobDetails.salary_range || formData.salary,
        industry: jobDetails.industry || formData.industry,
        roleType: jobDetails.roleType || formData.roleType,
        workEnvironment: jobDetails.workEnvironment || formData.workEnvironment,
        applicationDeadline: jobDetails.applicationDeadline || formData.applicationDeadline,
        postedDate: jobDetails.postedDate || formData.postedDate,
        summary: jobDetails.summary || formData.summary, // Populate summary
        // Set workplace based on job_type or location if it contains relevant keywords
        workplace: jobDetails.job_type?.toLowerCase().includes('remote') ? 'remote' :
                  jobDetails.job_type?.toLowerCase().includes('hybrid') ? 'hybrid' :
                  jobDetails.location?.toLowerCase().includes('remote') ? 'remote' :
                  jobDetails.location?.toLowerCase().includes('hybrid') ? 'hybrid' :
                  formData.workplace
      };
      
      setFormData(updatedFormData);
      
      // If a company was extracted, try to find a match in our list
      if (jobDetails.company_name) {
        const foundCompany = companies.find(c => 
          c.name.toLowerCase() === jobDetails.company_name.toLowerCase()
        );
        
        if (foundCompany) {
          setSelectedCompany(foundCompany);
          // Update the form data with the company ID as well
          setFormData(prev => ({
            ...prev, 
            companyId: foundCompany.id
          }));
        } else {
          // No matching company found, clear selection but keep the name
          setSelectedCompany(null);
          setCompanySearchQuery(jobDetails.company_name);
        }
      }
      
      toast.success('Job details extracted successfully');
      
    } catch (error) {
      console.error('Error fetching job details:', error);
      setFetchError(`Failed to extract job details: ${error.message}`);
      toast.error(`Failed to extract job details: ${error.message}`);
      
      // If extraction failed, provide a more helpful error message
      if (extractionMethod === 'url') {
        toast.error('URL extraction failed. Please copy the job description text from the website and paste it below.', {
          duration: 7000
        });
        setExtractionMethod('text');
        setFetchError('Many job websites prevent automatic extraction. Please copy the full job description from the website and paste it below.');
      } else {
        toast.error('Text extraction failed. Please ensure you\'ve included the complete job description.', {
          duration: 5000
        });
      }
    } finally {
      setIsFetchingDetails(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl transform transition-all w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4 border-b pb-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {job ? 'Edit Job' : 'Add New Job'}
            </h3>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-500"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {isLoading ? (
            <div className="animate-pulse flex flex-col space-y-4">
              <div className="h-6 bg-gray-200 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-12 bg-gray-200 rounded"></div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Job Details Extraction Section */}
              <div className="border-t border-gray-200 mt-6 pt-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Job Details Extraction</h3>
                <p className="mt-1 text-sm text-gray-500">Let AI extract job details from text.</p>
                
                {/* Extraction Method Toggle */}
                <div className="mt-4 flex space-x-4">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium rounded-md bg-gray-200 text-gray-500 cursor-not-allowed"
                    disabled
                    title="Coming soon!"
                  >
                    From URL (Coming Soon)
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white"
                    onClick={() => setExtractionMethod('text')}
                  >
                    From Text
                  </button>
                </div>
                
                {/* Text Input for Job Description */}
                <div className="mt-4 space-y-2">
                  <div>
                    <label htmlFor="jobText" className="block text-sm font-medium text-gray-700">
                      Job Description Text
                    </label>
                    <textarea
                      id="jobText"
                      value={jobText}
                      onChange={(e) => setJobText(e.target.value)}
                      rows={8}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Paste the job description text here..."
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleFetchJobDetails}
                      disabled={isFetchingDetails}
                      className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      Extract Details
                    </button>
                  </div>
                </div>

                {fetchError && (
                  <div className="text-red-500 text-sm mt-1">
                    {fetchError}
                  </div>
                )}
              </div>

              {/* AI Generated Summary */}
              <div className={`mt-4 p-3 border rounded-md shadow-sm ${formData.summary ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200'}`}>
                <label htmlFor="summary" className={`block text-sm font-medium ${formData.summary ? 'text-indigo-700' : 'text-gray-700'}`}>
                  AI Generated Summary
                </label>
                <textarea
                  id="summary"
                  name="summary"
                  rows={3} // Keep rows reasonable
                  value={formData.summary}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white disabled:bg-gray-100"
                  placeholder="Summary will be generated here after fetching details..."
                  readOnly // Consider making it read-only initially
                />
                <p className={`mt-1 text-xs ${formData.summary ? 'text-indigo-600' : 'text-gray-500'}`}>This summary is automatically generated by AI based on the job description URL.</p>
              </div>

              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                    Job Title*
                  </label>
                  <input
                    type="text"
                    name="title"
                    id="title"
                    required
                    value={formData.title}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                
                {/* Company Selection with Combobox */}
                <div>
                  <label htmlFor="company-select" className="block text-sm font-medium text-gray-700">
                    Company*
                  </label>
                  <div className="relative mt-1">
                    <Combobox value={selectedCompany} onChange={handleSelectCompany}>
                      <div className="relative">
                        <Combobox.Input
                          id="company-select"
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          placeholder="Search for company or add new..."
                          displayValue={(company) => {
                            // Always display current form data company name if no company is selected
                            // This ensures we don't lose the text when searching
                            return company?.name || formData.company;
                          }}
                          onChange={(event) => {
                            // Only update search query - never modify actual form data
                            setCompanySearchQuery(event.target.value);
                            setCompanyFieldTouched(true);
                          }}
                          onFocus={() => {
                            // When focusing, initialize search with current company name if not already touched
                            if (!companyFieldTouched && formData.company) {
                              setCompanySearchQuery(formData.company);
                            }
                          }}
                          onClick={(e) => {
                            // Prevent default behavior that might reset form
                            e.stopPropagation();
                          }}
                        />
                        <Combobox.Button 
                          className="absolute inset-y-0 right-0 flex items-center pr-2"
                          onClick={(e) => {
                            // Prevent default behavior that might reset form
                            e.stopPropagation();
                          }}
                        >
                          <ChevronDownIcon
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                        </Combobox.Button>
                      </div>
                      <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {filteredCompanies.length === 0 && companySearchQuery !== '' ? (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            No companies found.
                          </div>
                        ) : (
                          filteredCompanies.map((company) => (
                            <Combobox.Option
                              key={company.id}
                              value={company}
                              className={({ active }) =>
                                `relative cursor-default select-none py-2 pl-3 pr-9 ${
                                  active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                }`
                              }
                            >
                              {({ active, selected }) => (
                                <>
                                  <div className="flex items-center">
                                    <span className={`block truncate font-medium`}>
                                      {company.name}
                                    </span>
                                    {company.industry && (
                                      <span className={`ml-2 text-xs ${active ? 'text-indigo-100' : 'text-gray-500'}`}>
                                        ({company.industry})
                                      </span>
                                    )}
                                  </div>
                                </>
                              )}
                            </Combobox.Option>
                          ))
                        )}
                        <div 
                          className="relative cursor-pointer py-2 pl-3 pr-9 text-left text-indigo-600 hover:bg-indigo-50 flex items-center gap-1 border-t border-gray-100"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent triggering Combobox selection
                            setIsAddCompanyModalOpen(true);
                          }}
                        >
                          <PlusCircleIcon className="h-4 w-4" aria-hidden="true" />
                          <span>Add New Company</span>
                        </div>
                      </Combobox.Options>
                    </Combobox>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    id="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                    Job Type
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="freelance">Freelance</option>
                    <option value="internship">Internship</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="source" className="block text-sm font-medium text-gray-700">
                    Source
                  </label>
                  <select
                    id="source"
                    name="source"
                    value={formData.source}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                    <option value="employer">Employer</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Full Job Description*
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  required
                  value={formData.description}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="requirements" className="block text-sm font-medium text-gray-700">
                  Requirements
                </label>
                <textarea
                  id="requirements"
                  name="requirements"
                  rows={3}
                  value={formData.requirements}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter requirements, one per line"
                />
                <p className="mt-1 text-xs text-gray-500">Enter requirements, one per line</p>
              </div>
              
              <div>
                <label htmlFor="responsibilities" className="block text-sm font-medium text-gray-700">
                  Responsibilities
                </label>
                <textarea
                  id="responsibilities"
                  name="responsibilities"
                  rows={3}
                  value={formData.responsibilities}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter responsibilities, one per line"
                />
                <p className="mt-1 text-xs text-gray-500">Enter responsibilities, one per line</p>
              </div>

              {/* Grid 2: Experience Level, Salary, Industry, Role Type */}
              <h4 className="text-md font-medium text-gray-800 mt-4 border-t pt-4">Additional Details</h4>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="experienceLevel" className="block text-sm font-medium text-gray-700">
                    Experience Level
                  </label>
                  <select
                    id="experienceLevel"
                    name="experienceLevel"
                    value={formData.experienceLevel}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">Not Specified</option>
                    <option value="entry">Entry</option>
                    <option value="mid">Mid</option>
                    <option value="senior">Senior</option>
                    <option value="lead">Lead</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="salary" className="block text-sm font-medium text-gray-700">
                    Salary / Compensation
                  </label>
                  <input
                    type="text"
                    name="salary"
                    id="salary"
                    value={formData.salary}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="e.g., $100k - $120k, Competitive"
                  />
                </div>

                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700">
                    Industry
                  </label>
                  <input
                    type="text"
                    name="industry"
                    id="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="e.g., Technology, Finance, Healthcare"
                  />
                </div>

                <div>
                  <label htmlFor="roleType" className="block text-sm font-medium text-gray-700">
                    Role Type
                  </label>
                  <select
                    id="roleType"
                    name="roleType"
                    value={formData.roleType}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">Not Specified</option>
                    <option value="technical">Technical</option>
                    <option value="non-technical">Non-Technical</option>
                    <option value="management">Management</option>
                    <option value="sales">Sales</option>
                    <option value="marketing">Marketing</option>
                    <option value="design">Design</option>
                    <option value="operations">Operations</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="workEnvironment" className="block text-sm font-medium text-gray-700">
                  Work Environment
                </label>
                <textarea
                  id="workEnvironment"
                  name="workEnvironment"
                  rows={2}
                  value={formData.workEnvironment}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="e.g., Fast-paced startup, Collaborative team, Flexible hours"
                />
              </div>

              <div>
                <label htmlFor="workplace" className="block text-sm font-medium text-gray-700">
                  Workplace
                </label>
                <select
                  id="workplace"
                  name="workplace"
                  value={formData.workplace}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">Select workplace type</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="on-site">On-site</option>
                </select>
              </div>

              {/* Grid 3: Dates */}
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="postedDate" className="block text-sm font-medium text-gray-700">
                    Posted Date
                  </label>
                  <input
                    type="text" // Could be date, but extracted value might be relative like '2 days ago'
                    name="postedDate"
                    id="postedDate"
                    value={formData.postedDate}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="e.g., YYYY-MM-DD or '2 days ago'"
                  />
                </div>

                <div>
                  <label htmlFor="applicationDeadline" className="block text-sm font-medium text-gray-700">
                    Application Deadline
                  </label>
                  <input
                    type="text" // Could be date type if format is guaranteed
                    name="applicationDeadline"
                    id="applicationDeadline"
                    value={formData.applicationDeadline}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="e.g., YYYY-MM-DD"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 border-t pt-6">
                <button
                  type="button"
                  className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : (job ? 'Update Job' : 'Add Job')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Add Company Modal */}
      <AddCompanyModalPortal 
        isOpen={isAddCompanyModalOpen}
        onClose={() => setIsAddCompanyModalOpen(false)}
        onCompanyAdded={handleAddNewCompany}
      />
    </>
  );
} 